import React, {
  useState, useEffect, lazy, Suspense,
} from 'react';
import { useIntl } from 'react-intl';
import { Thumbnail, ThumbnailProps, TileButton } from '@printdeal/ui-library';
import axios from 'axios';
import { isAlternativeUploadsUrl } from '@printdeal/business-logic';
import { CimDoc } from '@design-stack-ct/cdif-types/v2';
// @ts-expect-error
import { messages } from '../translations/orders';
import ClientOnly from '../../ClientOnly';
import LineItem from '../../../types/cart/LineItem';

const PreviewModalContainer = lazy(() => import(/* webpackChunkName: "preview-modal" */'./modals/PreviewModalContainer'));

interface ArtworkThumbnailProps {
  lineItem?: LineItem;
  documentReference?: string | undefined,
  uploadsUrl?: string | undefined,
  isDeliveredFileValid?: boolean,
  userSelection?: string[],
  clickedCallback?: () => void,
  size?: ThumbnailProps['size'];
  className?: string;
}

const getRenderingUrl = (documentReference: string | undefined, uploadsURL: string | undefined) => {
  if (documentReference) {
    return `https://previews.cimpress.io/v2/preview?documentReferenceUri=${encodeURIComponent(documentReference)}&width=400`;
  }
  return uploadsURL as string;
};

const ArtworkThumbnail = ({
  lineItem,
  uploadsUrl,
  documentReference,
  isDeliveredFileValid = false,
  userSelection,
  clickedCallback = () => {},
  size = 'sm',
  className = '',
}: ArtworkThumbnailProps) => {
  const [cimDoc, setCimDoc] = useState<CimDoc | undefined>(undefined);
  const [path, setPath] = useState<string | undefined>(undefined);
  const [showPreview, setShowPreview] = useState(false);
  const intl = useIntl();
  const alternativeUploadsUrl = lineItem ? isAlternativeUploadsUrl(lineItem) : null;
  const showPreviewModal = cimDoc && !['bespoke', 'hybrid'].includes(lineItem?.custom.fields.quoteProductType || '');

  const getDocument = async (documentReferenceUrl: string) => {
    const { data: docref } = await axios.get(documentReferenceUrl);
    const { data: cimDocument } = await axios.get(docref.cimDocUrl);
    setCimDoc(cimDocument);
  };

  const getThumbnailSource = async () => {
    try {
      const retrievedPath = getRenderingUrl(documentReference, uploadsUrl);
      await axios.get(retrievedPath); // Check if retrieved path really works
      setPath(retrievedPath);
    } catch (error) {
      // Do nothing
    }
  };

  useEffect(() => {
    if (documentReference || uploadsUrl) {
      getThumbnailSource();
    }
    setPath(undefined);
  }, [documentReference, uploadsUrl]);

  useEffect(() => {
    if (documentReference) {
      getDocument(documentReference);
    }
  }, []);

  const onPreviewClick = (event: Event) => {
    event.preventDefault();
    setShowPreview(true);
    clickedCallback();
  };

  let fileName: string;
  let thumbnailIcon: 'exclamation' | 'file-pdf' | 'image' | null;
  let thumbnailText = '';

  if (lineItem?.custom?.fields?.userUploadsUrl) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    const { userUploadsUrl } = lineItem?.custom?.fields;
    userUploadsUrl
      .map((string: string) => string.split('::'))
      .map((subStr: string[]) => {
        const uploadedFileName = subStr[1];
        if (uploadedFileName.length <= 15) {
          // eslint-disable-next-line prefer-destructuring
          fileName = uploadedFileName;
        } else {
          const fileExtention = uploadedFileName.lastIndexOf('.');
          fileName = `${uploadedFileName.substring(0, 7)}...${uploadedFileName.slice(fileExtention - 2)}`;
        }
        return fileName;
      });
  }

  if (!isDeliveredFileValid) {
    thumbnailIcon = 'exclamation';
    thumbnailText = intl.formatMessage(messages.fileInvalid);
  } else if (lineItem?.custom?.fields?.userUploadsUrl && !alternativeUploadsUrl) {
    thumbnailIcon = 'file-pdf';
    // @ts-expect-error
    thumbnailText = fileName;
  } else {
    thumbnailIcon = 'image';
  }

  return (
    <>
      {!alternativeUploadsUrl && !path ? (
        <TileButton
          size={size}
          disabled
          icon={thumbnailIcon}
          className={className}
        />
      ) : (
        <Thumbnail
          size={size}
          data-cy="thumbFile"
          className={className}
          button={showPreviewModal ? (
            <Thumbnail.Button
              screenReaderLabel="Zoom"
              onClick={onPreviewClick}
            />
          ) : undefined}
        >
          <img src={alternativeUploadsUrl || path} alt={thumbnailText} />
        </Thumbnail>
      )}
      {showPreviewModal && cimDoc?.sku && userSelection && documentReference && lineItem?.custom?.fields.productKey && (
        <ClientOnly>
          <Suspense fallback={null}>
            <PreviewModalContainer
              documentReferenceUrl={documentReference}
              setIsOpen={setShowPreview}
              isOpen={showPreview}
              mcpSku={cimDoc.sku}
              productKey={lineItem?.custom?.fields.productKey}
              userSelection={userSelection}
            />
          </Suspense>
        </ClientOnly>
      )}
    </>
  );
};

export default ArtworkThumbnail;
